"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";

function getRangeWithDots(
  current: number,
  last: number,
  delta: number,
): (number | "...")[] {
  const left = current - delta + 1;
  const right = current + delta + 1;
  const range: number[] = [];
  const rangeWithDots: (number | "...")[] = [];
  let l = 0;

  for (let i = 1; i <= last; i++) {
    if (i === 1 || i === last || (i >= left && i <= right)) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push("...");
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
}

const btnBase =
  "w-8 h-8 flex items-center justify-center border rounded-md text-sm transition-colors";
const btnDefault = "border-gray-200 bg-white hover:bg-gray-50 cursor-pointer";
const btnDisabled = "border-gray-200 bg-white text-gray-300 cursor-not-allowed";
const btnSelected = "border-iq-orange bg-iq-orange-light text-iq-orange";

export function Paginator({
  page,
  pageSize,
  total,
  hrefBuilder,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  hrefBuilder: (page: number) => string;
  onPageChange?: (page: number) => void;
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  const canGoBack = page > 0;
  const canGoForth = page < totalPages - 1;
  const pages = getRangeWithDots(page, totalPages, 2);

  function handleClick(e: React.MouseEvent, targetPage: number) {
    if (!onPageChange) return;
    e.preventDefault();
    onPageChange(targetPage);
  }

  return (
    <div className="flex items-center gap-1 select-none">
      {canGoBack ? (
        <Link
          href={hrefBuilder(page - 1)}
          onClick={(e) => handleClick(e, page - 1)}
          className={`${btnBase} ${btnDefault}`}
        >
          <ChevronLeft size={16} />
        </Link>
      ) : (
        <span className={`${btnBase} ${btnDisabled}`}>
          <ChevronLeft size={16} />
        </span>
      )}

      {pages.map((entry, i) => {
        if (entry === "...") {
          return (
            <span
              key={`ellipsis-${i}`}
              className="w-8 h-8 flex items-center justify-center text-sm text-gray-500"
            >
              …
            </span>
          );
        }
        const isSelected = entry - 1 === page;
        if (isSelected) {
          return (
            <span key={entry} className={`${btnBase} ${btnSelected}`}>
              {entry}
            </span>
          );
        }
        return (
          <Link
            key={entry}
            href={hrefBuilder(entry - 1)}
            onClick={(e) => handleClick(e, entry - 1)}
            className={`${btnBase} ${btnDefault}`}
          >
            {entry}
          </Link>
        );
      })}

      {canGoForth ? (
        <Link
          href={hrefBuilder(page + 1)}
          onClick={(e) => handleClick(e, page + 1)}
          className={`${btnBase} ${btnDefault}`}
        >
          <ChevronRight size={16} />
        </Link>
      ) : (
        <span className={`${btnBase} ${btnDisabled}`}>
          <ChevronRight size={16} />
        </span>
      )}
    </div>
  );
}
